import React from 'react';
import { VaultKeTheme } from '../../utils/theme';

const Security: React.FC = () => {
  const styles: { [key: string]: React.CSSProperties } = {
    page: {
      minHeight: '100vh',
      backgroundColor: VaultKeTheme.colors.background,
      color: VaultKeTheme.colors.text,
    },
    main: {
      paddingTop: '80px',
      paddingBottom: VaultKeTheme.spacing.xxl,
    },
    container: {
      maxWidth: '1200px',
      margin: '0 auto',
      padding: `0 ${VaultKeTheme.spacing.xl}`,
    },
    header: {
      textAlign: 'center',
      marginBottom: VaultKeTheme.spacing.xxl,
    },
    title: {
      ...VaultKeTheme.typography.h1,
      color: VaultKeTheme.colors.text,
      marginBottom: VaultKeTheme.spacing.lg,
    },
    subtitle: {
      ...VaultKeTheme.typography.body1,
      color: VaultKeTheme.colors.textSecondary,
      maxWidth: '600px',
      margin: '0 auto',
    },
    securityGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
      gap: VaultKeTheme.spacing.xl,
      marginBottom: VaultKeTheme.spacing.xxl,
    },
    securityCard: {
      ...VaultKeTheme.components.card,
      textAlign: 'center',
    },
    securityIcon: {
      fontSize: '3rem',
      marginBottom: VaultKeTheme.spacing.md,
      color: VaultKeTheme.colors.primary,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    },
    securityTitle: {
      ...VaultKeTheme.typography.h4,
      color: VaultKeTheme.colors.text,
      marginBottom: VaultKeTheme.spacing.sm,
    },
    securityDesc: {
      ...VaultKeTheme.typography.body2,
      color: VaultKeTheme.colors.textSecondary,
      lineHeight: '1.6',
    },
    compliance: {
      ...VaultKeTheme.components.card,
      marginBottom: VaultKeTheme.spacing.xxl,
    },
    complianceTitle: {
      ...VaultKeTheme.typography.h3,
      color: VaultKeTheme.colors.text,
      marginBottom: VaultKeTheme.spacing.lg,
      textAlign: 'center',
    },
    certifications: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: VaultKeTheme.spacing.lg,
    },
    cert: {
      textAlign: 'center',
      padding: VaultKeTheme.spacing.lg,
      background: `linear-gradient(135deg, ${VaultKeTheme.colors.surface} 0%, ${VaultKeTheme.colors.surfaceVariant} 100%)`,
      borderRadius: VaultKeTheme.borderRadius.lg,
    },
    certIcon: {
      fontSize: '2rem',
      marginBottom: VaultKeTheme.spacing.sm,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    },
    certTitle: {
      ...VaultKeTheme.typography.h5,
      color: VaultKeTheme.colors.text,
      marginBottom: VaultKeTheme.spacing.sm,
    },
    certDesc: {
      ...VaultKeTheme.typography.caption,
      color: VaultKeTheme.colors.textSecondary,
    },
    stats: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: VaultKeTheme.spacing.xl,
      marginBottom: VaultKeTheme.spacing.xxl,
    },
    stat: {
      textAlign: 'center',
      ...VaultKeTheme.components.card,
    },
    statNumber: {
      ...VaultKeTheme.typography.h2,
      color: VaultKeTheme.colors.primary,
      marginBottom: VaultKeTheme.spacing.sm,
    },
    statLabel: {
      ...VaultKeTheme.typography.body2,
      color: VaultKeTheme.colors.textSecondary,
    },
  };

  const securityFeatures = [
    {
      icon: '🔐',
      title: 'End-to-End Encryption',
      description: 'All data is encrypted in transit and at rest using industry-standard AES-256 encryption protocols.',
    },
    {
      icon: '🛡️',
      title: 'Multi-Factor Authentication',
      description: 'Enhanced security with SMS, email, and authenticator app verification for all user accounts.',
    },
    {
      icon: '👁️',
      title: 'Zero-Trust Architecture',
      description: 'Every request is authenticated and authorized, ensuring no unauthorized access to sensitive data.',
    },
    {
      icon: '🔍',
      title: 'Continuous Monitoring',
      description: '24/7 security monitoring and automated threat detection to identify and respond to potential issues.',
    },
    {
      icon: '💳',
      title: 'Secure Payments',
      description: 'PCI DSS compliant payment processing with tokenized card storage and secure transaction handling.',
    },
    {
      icon: '📋',
      title: 'Audit Trails',
      description: 'Complete logging of all financial transactions and user activities for transparency and compliance.',
    },
  ];

  const certifications = [
    {
      icon: '🏛️',
      title: 'ISO 27001',
      description: 'Information Security Management',
    },
    {
      icon: '🔒',
      title: 'SOC 2 Type II',
      description: 'Security, Availability & Confidentiality',
    },
    {
      icon: '💳',
      title: 'PCI DSS',
      description: 'Payment Card Industry Standard',
    },
    {
      icon: '🇰🇪',
      title: 'CBK Compliant',
      description: 'Central Bank of Kenya Standards',
    },
  ];

  return (
    <div style={styles.page}>
      <main style={styles.main}>
        <div style={styles.container}>
          <div style={styles.header}>
            <h1 style={styles.title}>Security & Trust</h1>
            <p style={styles.subtitle}>
              Your financial data and community funds are protected by bank-grade security
              measures and industry-leading compliance standards.
            </p>
          </div>

          <div style={styles.stats}>
            <div style={styles.stat}>
              <div style={styles.statNumber}>99.9%</div>
              <div style={styles.statLabel}>Uptime</div>
            </div>
            <div style={styles.stat}>
              <div style={styles.statNumber}>256-bit</div>
              <div style={styles.statLabel}>AES Encryption</div>
            </div>
            <div style={styles.stat}>
              <div style={styles.statNumber}>24/7</div>
              <div style={styles.statLabel}>Security Monitoring</div>
            </div>
            <div style={styles.stat}>
              <div style={styles.statNumber}>0</div>
              <div style={styles.statLabel}>Data Breaches</div>
            </div>
          </div>

          <div style={styles.securityGrid}>
            {securityFeatures.map((feature, index) => (
              <div key={index} style={styles.securityCard}>
                <div style={styles.securityIcon}>{feature.icon}</div>
                <h3 style={styles.securityTitle}>{feature.title}</h3>
                <p style={styles.securityDesc}>{feature.description}</p>
              </div>
            ))}
          </div>

          <div style={styles.compliance}>
            <h3 style={styles.complianceTitle}>Industry Certifications & Compliance</h3>
            <div style={styles.certifications}>
              {certifications.map((cert, index) => (
                <div key={index} style={styles.cert}>
                  <div style={styles.certIcon}>{cert.icon}</div>
                  <h4 style={styles.certTitle}>{cert.title}</h4>
                  <p style={styles.certDesc}>{cert.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Security;